import type { ILead, IProperty } from 'src/services/api';
import { VOUCHER_TYPE_OPTIONS } from 'src/services/api/leads/lead-facts';

export type ISimilarityFilters = {
    radiusMiles: number;
    /** Match this property type exactly; null means any type. */
    propertyType: IProperty['type'];
    rentMin: number | null;
    rentMax: number | null;
    bedroomsMin: number | null;
    bedroomsMax: number | null;
    bathroomsMin: number | null;
    isVacantOnly: boolean;
};

export type ISimilarProperty = {
    property: IProperty;
    /**
     * Distance from the anchor, or null when there is no geocoded anchor to measure
     * from and the results are therefore not distance-ranked.
     */
    distanceMiles: number | null;
    /** 0..1 weighted fit, driving the sort order. See {@link scoreSimilarity}. */
    score: number;
};

export const RADIUS_OPTIONS = [1, 3, 5] as const;

/** Distance beyond which a match no longer counts toward the score at all. */
export const MAX_RADIUS_MILES = 5;

const EARTH_RADIUS_MILES = 3958.8;
const DEG_TO_RAD = Math.PI / 180;

/**
 * Great-circle distance. Exact enough at neighbourhood scale and cheap enough to run
 * over every property in a team on each filter change.
 */
export const distanceInMiles = (
    latA: number,
    lngA: number,
    latB: number,
    lngB: number
): number => {
    const dLat = (latB - latA) * DEG_TO_RAD;
    const dLng = (lngB - lngA) * DEG_TO_RAD;
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(latA * DEG_TO_RAD) * Math.cos(latB * DEG_TO_RAD) * Math.sin(dLng / 2) ** 2;
    return 2 * EARTH_RADIUS_MILES * Math.asin(Math.min(1, Math.sqrt(a)));
};

const RENT_TOLERANCE = 0.2;
const BEDROOM_TOLERANCE = 1;
const DEFAULT_RADIUS_MILES = 5;

export type ILatLng = { latitude: number; longitude: number };

/**
 * The point to measure distances from, or null when there is nothing to measure from:
 * either the lead has no assigned property, or that property has never been geocoded.
 */
export const getAnchorPosition = (anchor: IProperty | null): ILatLng | null => (
    anchor != null && anchor.latitude != null && anchor.longitude != null ?
        { latitude: anchor.latitude, longitude: anchor.longitude } :
        null
);

/** Lead-side signals; independent of UI filter state. */
export type ILeadSignals = {
    /** Target monthly rent, or null when unknowable. */
    monthlyBudget: number | null;
    /** Species, or null when the lead has no pet or it is unknown. */
    petType: string | null;
    /** The lead's own voucher_type value, or null when the lead has none on file. */
    voucherType: string | null;
};

/**
 * Derives the signals that drive ranking (budget) and hard filtering (pets, vouchers)
 * from the lead, falling back to the anchor property where the lead itself is silent.
 *
 * annual_income is the standard 3x-rent qualification rule expressed as monthly figures:
 * (annual_income / 12) / 3 == annual_income / 36.
 */
export const getLeadSignals = (
    lead: Pick<ILead, 'annual_income' | 'pet_type' | 'voucher_type'> | null,
    anchor: IProperty | null
): ILeadSignals => ({
    monthlyBudget:
        lead?.annual_income != null ? Math.round(lead.annual_income / 36) :
            anchor?.rent ?? null,
    petType:
        lead?.pet_type != null && lead.pet_type !== 'none' && lead.pet_type !== 'unspecified' ?
            lead.pet_type :
            null,
    voucherType: lead?.voucher_type ?? null,
});

/**
 * Conservative match for a pet policy that clearly forbids pets. Anything ambiguous
 * (e.g. "cats only", "$50/mo pet fee") is left alone — the filter only ever hides a
 * candidate it is sure about.
 */
const GENERIC_NO_PETS_RE = /\bno\s+(pets?|animals?)\b|\bpets?\s+(are\s+)?not\s+(allowed|permitted|accepted)\b|\bpet[-\s]?free\b/i;

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const isPetExcluded = (petType: string, petPolicy: string): boolean => {
    // A ban on a different species is not a ban on this lead's pet: "no dogs" must not
    // hide a property from a cat owner, so only the lead's own species is matched.
    const species = escapeRegExp(petType.trim().toLowerCase().replace(/s$/, ''));
    const speciesBanRe = species === '' ?
        null :
        new RegExp(`\\bno\\s+${species}s?\\b`, 'i');
    return GENERIC_NO_PETS_RE.test(petPolicy) ||
        (speciesBanRe != null && speciesBanRe.test(petPolicy));
};

/** The lead-facing voucher vocabulary, mapped to the backend `AcceptedVoucherType` values `accepted_vouchers` is drawn from. */
export const LEAD_TO_PROPERTY_VOUCHER: Record<string, string> = {
    section_8: 'section_8_hcv',
    vash: 'hud_vash',
    other: 'other',
};

/**
 * Conservative match for a voucher ban: only an explicit, structured list that omits
 * the lead's mapped voucher type excludes a property. A null list means the property's
 * acceptance is unknown, so it is kept rather than penalized, and an unmappable lead
 * value (should not happen given the fixed vocabulary, but the record lookup is not
 * exhaustively typed) is likewise never grounds to exclude.
 */
export const isVoucherExcluded = (voucherType: string, acceptedVouchers: string[] | null): boolean => {
    const mapped = LEAD_TO_PROPERTY_VOUCHER[voucherType];
    return mapped != null && acceptedVouchers != null && !acceptedVouchers.includes(mapped);
};

/**
 * Weighted 0..1 fit score, used to rank matches: budget carries the most weight since
 * it is the hardest constraint for an agent to work around, floor plan next, distance
 * least since the radius filter already bounds it. Every component defaults to a
 * neutral 0.5 rather than penalizing unknown data on either side.
 */
export const scoreSimilarity = (
    property: IProperty,
    distanceMiles: number | null,
    anchor: IProperty | null,
    signals?: ILeadSignals
): number => {
    const target = signals?.monthlyBudget ?? null;
    const budgetScore = target == null || property.rent == null ?
        0.5 :
        Math.max(0, 1 - Math.abs(property.rent - target) / target);

    const bedScore = anchor?.num_bedrooms == null || property.num_bedrooms == null ?
        0.5 :
        anchor.num_bedrooms === property.num_bedrooms ? 1 :
            Math.abs(anchor.num_bedrooms - property.num_bedrooms) === 1 ? 0.5 :
                0;
    const bathScore = anchor?.num_bathrooms == null || property.num_bathrooms == null ?
        0.5 :
        anchor.num_bathrooms === property.num_bathrooms ? 1 :
            Math.abs(anchor.num_bathrooms - property.num_bathrooms) <= 1 ? 0.5 :
                0;
    const floorPlanScore = 0.6 * bedScore + 0.4 * bathScore;

    const distanceScore = distanceMiles == null ?
        0.5 :
        1 - Math.min(distanceMiles, MAX_RADIUS_MILES) / MAX_RADIUS_MILES;

    return 0.5 * budgetScore + 0.3 * floorPlanScore + 0.2 * distanceScore;
};

/**
 * Plain-language reasons an agent could say out loud to a lead, derived from the same
 * thresholds {@link scoreSimilarity} weighs numerically. Ordered budget, floor plan,
 * pets, voucher, distance — the same priority the score gives them — and capped at 3 so
 * the details panel and row tooltips stay a glance, not a pitch. An unknown or
 * unfavourable signal on either side earns no reason rather than a hedge; the array can
 * legitimately come back empty.
 */
export const explainFit = (
    property: IProperty,
    distanceMiles: number | null,
    anchor: IProperty | null,
    signals?: ILeadSignals
): string[] => {
    const reasons: string[] = [];

    const budget = signals?.monthlyBudget ?? null;
    if (budget != null && property.rent != null) {
        const deviation = Math.abs(property.rent - budget) / budget;
        if (deviation <= 0.05) {
            reasons.push(`right at their ~$${formatMoney(budget)}/mo budget`);
        } else if (property.rent < budget && deviation <= RENT_TOLERANCE) {
            reasons.push(`under their ~$${formatMoney(budget)}/mo budget`);
        } else if (property.rent > budget && deviation <= RENT_TOLERANCE) {
            // never spin a fit that is actually more than 20% over budget
            reasons.push(`close to their ~$${formatMoney(budget)}/mo budget`);
        }
    }

    if (anchor?.num_bedrooms != null && property.num_bedrooms != null) {
        const bedDiff = Math.abs(anchor.num_bedrooms - property.num_bedrooms);
        if (bedDiff === 0) {
            const bathsMatch = anchor.num_bathrooms != null &&
                property.num_bathrooms != null &&
                anchor.num_bathrooms === property.num_bathrooms;
            reasons.push(
                `same size — ${property.num_bedrooms} bd${bathsMatch ? ` / ${property.num_bathrooms} ba` : ''}`
            );
        } else if (bedDiff === 1) {
            reasons.push(`similar size — ${property.num_bedrooms} bd vs their ${anchor.num_bedrooms} bd`);
        }
    }

    if (signals?.petType != null) {
        const petPolicy = property.facts?.pet_policy?.value;
        if (typeof petPolicy === 'string' && petPolicy !== '' && !isPetExcluded(signals.petType, petPolicy)) {
            reasons.push(`pet policy covers their ${signals.petType}`);
        }
    }

    if (signals?.voucherType != null) {
        const mapped = LEAD_TO_PROPERTY_VOUCHER[signals.voucherType];
        if (mapped != null && property.accepted_vouchers?.includes(mapped)) {
            const label = VOUCHER_TYPE_OPTIONS.find((option) => option.value === signals.voucherType)?.label ??
                signals.voucherType;
            reasons.push(`accepts their ${label} voucher`);
        }
    }

    if (distanceMiles != null) {
        if (distanceMiles < 0.1) {
            reasons.push('same address');
        } else if (distanceMiles <= 1) {
            reasons.push('under a mile away');
        } else {
            reasons.push(`${formatDistance(distanceMiles)} away`);
        }
    }

    return reasons.slice(0, 3);
};

/**
 * Seed the filters from the lead's own property so the modal opens on a useful answer
 * rather than an empty form: comparable rent, comparable size, nearby, and available.
 *
 * With no assigned property there is nothing to be comparable to, so only the vacancy
 * filter is applied and the view becomes "every vacant property the team has".
 */
export const getDefaultFilters = (anchor: IProperty | null): ISimilarityFilters => ({
    radiusMiles: DEFAULT_RADIUS_MILES,
    /*
     * A lead assigned to a house is house-hunting: same-type matches are the useful
     * default, and the toolbar makes the constraint visible and removable.
     */
    propertyType: anchor?.type ?? null,
    rentMin: anchor?.rent != null ?
        Math.floor((anchor.rent * (1 - RENT_TOLERANCE)) / 50) * 50 :
        null,
    rentMax: anchor?.rent != null ?
        Math.ceil((anchor.rent * (1 + RENT_TOLERANCE)) / 50) * 50 :
        null,
    bedroomsMin: anchor?.num_bedrooms != null ?
        Math.max(0, anchor.num_bedrooms - BEDROOM_TOLERANCE) :
        null,
    bedroomsMax: anchor?.num_bedrooms != null ?
        anchor.num_bedrooms + BEDROOM_TOLERANCE :
        null,
    bathroomsMin: null,
    isVacantOnly: true,
});

/**
 * Rank properties by fit — budget, floor plan, and distance — keeping only those inside
 * the filters. The anchor itself is always excluded.
 *
 * Without a geocoded anchor the radius is not applied, but results are still
 * score-sorted, so a lead with no assigned property still gets the team's vacant
 * inventory ranked by what an agent can actually offer it against (budget, floor plan)
 * rather than an alphabetical list.
 *
 * Properties that have never been geocoded are always excluded, so the result list is
 * exactly what the caller can draw on a map. Their count is reported separately rather
 * than being dropped silently.
 */
export const findSimilarProperties = (
    anchor: IProperty | null,
    properties: IProperty[],
    filters: ISimilarityFilters,
    signals?: ILeadSignals
): { matches: ISimilarProperty[]; skippedWithoutCoordinates: number } => {
    const radiusMiles = Math.min(filters.radiusMiles, MAX_RADIUS_MILES);
    const anchorPosition = getAnchorPosition(anchor);

    const matches: ISimilarProperty[] = [];
    let skippedWithoutCoordinates = 0;

    for (const property of properties) {
        if (anchor != null && property.id === anchor.id) {
            continue;
        }
        if (property.latitude == null || property.longitude == null) {
            skippedWithoutCoordinates++;
            continue;
        }
        if (filters.isVacantOnly && !property.is_vacant) {
            continue;
        }
        if (filters.propertyType != null && property.type !== filters.propertyType) {
            continue;
        }
        // a property with unknown rent cannot be shown to satisfy a rent bound
        if (filters.rentMin != null && (property.rent == null || property.rent < filters.rentMin)) {
            continue;
        }
        if (filters.rentMax != null && (property.rent == null || property.rent > filters.rentMax)) {
            continue;
        }
        if (filters.bedroomsMin != null && (property.num_bedrooms ?? 0) < filters.bedroomsMin) {
            continue;
        }
        if (filters.bedroomsMax != null && (property.num_bedrooms ?? 0) > filters.bedroomsMax) {
            continue;
        }
        if (filters.bathroomsMin != null && (property.num_bathrooms ?? 0) < filters.bathroomsMin) {
            continue;
        }
        // a hard rule, not a toggle: unknown policies are always shown, only a clearly
        // stated ban hides a property from a lead with a pet
        if (signals?.petType != null) {
            const petPolicy = property.facts?.pet_policy?.value;
            if (typeof petPolicy === 'string' && isPetExcluded(signals.petType, petPolicy)) {
                continue;
            }
        }
        // same philosophy as the pet rule above: only an explicit mismatch hides a
        // property, an unknown accepted_vouchers list always leaves it shown
        if (signals?.voucherType != null && isVoucherExcluded(signals.voucherType, property.accepted_vouchers)) {
            continue;
        }

        let distanceMiles: number | null = null;
        if (anchorPosition) {
            distanceMiles = distanceInMiles(
                anchorPosition.latitude,
                anchorPosition.longitude,
                property.latitude,
                property.longitude
            );
            if (distanceMiles > radiusMiles) {
                continue;
            }
        }

        matches.push({ property, distanceMiles, score: scoreSimilarity(property, distanceMiles, anchor, signals) });
    }

    matches.sort((a, b) => (
        b.score - a.score ||
        (a.distanceMiles ?? Infinity) - (b.distanceMiles ?? Infinity) ||
        a.property.name.localeCompare(b.property.name)
    ));

    return { matches, skippedWithoutCoordinates };
};

export const formatDistance = (miles: number): string => {
    if (miles < 0.1) {
        return 'same address';
    }
    return miles < 10 ? `${miles.toFixed(1)} mi` : `${Math.round(miles)} mi`;
};

const formatMoney = (amount: number): string => amount.toLocaleString('en-US');

const pluralize = (count: number, singular: string, plural: string): string => (
    count === 1 ? singular : plural
);

/**
 * A word already ending in "s" is treated as an irregular plural (e.g. property type
 * "Mobile Home Lots") and left alone rather than doubled.
 */
const pluralizeLastWord = (words: string[]): string[] => {
    const last = words[words.length - 1];
    const pluralLast = /[^aeiou]y$/i.test(last) ?
        `${last.slice(0, -1)}ies` :
        /s$/i.test(last) ? last : `${last}s`;
    return [...words.slice(0, -1), pluralLast];
};

const pluralizeType = (type: NonNullable<ISimilarityFilters['propertyType']>): string => (
    pluralizeLastWord(type.toLowerCase().split(' ')).join(' ')
);

/** "$1,450–$2,150" / "up to $2,200" / "from $1,400" / null when neither bound is set. */
export const formatRentFragment = (
    filters: Pick<ISimilarityFilters, 'rentMin' | 'rentMax'>
): string | null => {
    if (filters.rentMin != null && filters.rentMax != null) {
        return `$${formatMoney(filters.rentMin)}–$${formatMoney(filters.rentMax)}`;
    }
    if (filters.rentMax != null) {
        return `up to $${formatMoney(filters.rentMax)}`;
    }
    if (filters.rentMin != null) {
        return `from $${formatMoney(filters.rentMin)}`;
    }
    return null;
};

/** "1–3 beds" / "2 beds" / "up to 3 beds" / "1+ beds" / null when neither bound is set. */
export const formatBedsFragment = (
    filters: Pick<ISimilarityFilters, 'bedroomsMin' | 'bedroomsMax'>
): string | null => {
    const { bedroomsMin, bedroomsMax } = filters;
    if (bedroomsMin != null && bedroomsMax != null) {
        return bedroomsMin === bedroomsMax ?
            `${bedroomsMin} ${pluralize(bedroomsMin, 'bed', 'beds')}` :
            `${bedroomsMin}–${bedroomsMax} beds`;
    }
    if (bedroomsMax != null) {
        return `up to ${bedroomsMax} ${pluralize(bedroomsMax, 'bed', 'beds')}`;
    }
    if (bedroomsMin != null) {
        return `${bedroomsMin}+ beds`;
    }
    return null;
};

/** "2+ baths" / null when unset. */
export const formatBathsFragment = (
    filters: Pick<ISimilarityFilters, 'bathroomsMin'>
): string | null => (
    filters.bathroomsMin != null ?
        `${filters.bathroomsMin}+ ${pluralize(filters.bathroomsMin, 'bath', 'baths')}` :
        null
);

/**
 * A plain-English readout of what the map is showing, so a non-technical user never has
 * to reconstruct the search from a scatter of numbers and toggles. Bathrooms are left out
 * on purpose: they already have their own always-visible chip, and folding a fifth clause
 * in here made the sentence harder to parse than the panel it was meant to simplify.
 *
 * The pet and voucher rules are hard filters, not a user choice, so each is left to its
 * own chip and tooltip in the toolbar rather than repeated here.
 */
export const formatSearchSentence = (
    _anchor: IProperty | null,
    filters: ISimilarityFilters,
    matches: ISimilarProperty[],
    isDistanceRanked: boolean,
    _signals?: ILeadSignals
): string => {
    const typeFragment = filters.propertyType != null ? pluralizeType(filters.propertyType) : 'properties';
    const subject = [filters.isVacantOnly ? 'vacant' : null, typeFragment].filter(Boolean).join(' ');
    const locationFragment = isDistanceRanked ? `within ${filters.radiusMiles} mi` : 'across the team';

    const clauses = [
        `${subject} ${locationFragment}`,
        formatRentFragment(filters),
        formatBedsFragment(filters),
    ].filter((clause): clause is string => clause != null);

    const showBestFit = matches.length > 1;

    return `Showing ${clauses.join(', ')}${showBestFit ? ' — best fit first.' : '.'}`;
};

export type IEmptyFix = {
    key: 'vacancy' | 'rent' | 'type' | 'bedrooms' | 'bathrooms';
    label: string;
    patch: Partial<ISimilarityFilters>;
    count: number;
};

/**
 * When a search comes back empty, tries lifting exactly one constraint at a time —
 * never the 5-mile ceiling or the pet/voucher rules, which are hard limits, not choices — and
 * reports which single lifts would actually produce results. Ordered gentlest-first
 * (smallest count leads) so the fix offered first changes the search the least.
 */
export const diagnoseEmptyResult = (
    anchor: IProperty | null,
    properties: IProperty[],
    filters: ISimilarityFilters,
    signals?: ILeadSignals
): IEmptyFix[] => {
    const candidates: {
        key: IEmptyFix['key'];
        isConstraining: boolean;
        patch: Partial<ISimilarityFilters>;
        label: (count: number) => string;
    }[] = [
        {
            key: 'vacancy',
            isConstraining: filters.isVacantOnly,
            patch: { isVacantOnly: false },
            label: (count) => `Include ${count} occupied ${pluralize(count, 'home', 'homes')}`,
        },
        {
            key: 'rent',
            isConstraining: filters.rentMin != null || filters.rentMax != null,
            patch: { rentMin: null, rentMax: null },
            label: (count) => `Show ${count} outside the rent range`,
        },
        {
            key: 'type',
            isConstraining: filters.propertyType != null,
            patch: { propertyType: null },
            label: (count) => `Any property type — ${count} more`,
        },
        {
            key: 'bedrooms',
            isConstraining: filters.bedroomsMin != null || filters.bedroomsMax != null,
            patch: { bedroomsMin: null, bedroomsMax: null },
            label: (count) => `Any bedroom count — ${count} more`,
        },
        {
            key: 'bathrooms',
            isConstraining: filters.bathroomsMin != null,
            patch: { bathroomsMin: null },
            label: (count) => `Any bathroom count — ${count} more`,
        },
    ];

    const fixes = candidates
        .filter((candidate) => candidate.isConstraining)
        .map((candidate) => {
            const { matches } = findSimilarProperties(
                anchor,
                properties,
                { ...filters, ...candidate.patch },
                signals
            );
            return {
                key: candidate.key,
                label: candidate.label(matches.length),
                patch: candidate.patch,
                count: matches.length,
            };
        })
        .filter((fix) => fix.count > 0)
        .sort((a, b) => a.count - b.count);

    return fixes.slice(0, 3);
};
